/**
 * SLA Monitoring Service
 * 
 * Tracks and monitors Service Level Agreement targets for:
 * - Onboarding completion
 * - Credit application decisions
 * - Document reviews
 * - Role completions
 */

import { supabase } from '../integrations/supabase/client';
import { BusinessRulesEngine } from './businessRulesEngine';
import type { Database } from '../integrations/supabase/types';

type SlaTrackingRow = Database['public']['Tables']['sla_tracking']['Row'];
type SlaTrackingInsert = Database['public']['Tables']['sla_tracking']['Insert'];

export type SlaType = 
  | 'onboarding_completion'
  | 'credit_decision'
  | 'document_review'
  | 'role_completion';

export type SlaStatus = 'pending' | 'met' | 'breached' | 'at_risk' | 'cancelled';

export interface SlaRecord {
  id: string;
  entityType: string;
  entityId: string;
  slaType: SlaType;
  targetDate: Date;
  actualDate: Date | null;
  status: SlaStatus;
  breachNotifiedAt: Date | null;
  warningNotifiedAt: Date | null;
  platformTenantId: string;
  clientId: string | null;
  metadata: Record<string, unknown>;
  createdAt: Date;
}

export interface SlaStats {
  total: number;
  pending: number;
  met: number;
  breached: number;
  atRisk: number;
  metPercentage: number;
}

class SlaMonitoringServiceClass {
  /**
   * Create an SLA tracking record
   */
  async createSla(params: {
    entityType: string;
    entityId: string;
    slaType: SlaType;
    platformTenantId: string;
    clientId?: string;
    metadata?: Record<string, unknown>;
  }): Promise<SlaRecord> {
    // Get SLA target from business rules
    const slaTarget = await this.getSlaTargetHours(params.platformTenantId, params.slaType);
    const targetDate = new Date(Date.now() + slaTarget * 60 * 60 * 1000);

    const insertData = {
      entity_type: params.entityType,
      entity_id: params.entityId,
      sla_type: params.slaType,
      target_date: targetDate.toISOString(),
      platform_tenant_id: params.platformTenantId,
      client_id: params.clientId || null,
      metadata: params.metadata || {},
      status: 'pending',
    } as const;

    const { data, error } = await supabase
      .from('sla_tracking')
      .insert(insertData as SlaTrackingInsert)
      .select()
      .single();

    if (error) {
      // If duplicate, return existing
      if (error.code === '23505') {
        const existing = await this.getSla(params.entityType, params.entityId, params.slaType);
        if (existing) return existing;
      }
      throw new Error(`Failed to create SLA: ${error.message}`);
    }

    return this.mapSlaRecord(data);
  }

  /**
   * Get SLA record
   */
  async getSla(entityType: string, entityId: string, slaType: SlaType): Promise<SlaRecord | null> {
    const { data, error } = await supabase
      .from('sla_tracking')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('sla_type', slaType)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get SLA: ${error.message}`);
    }

    return data ? this.mapSlaRecord(data) : null;
  }

  /**
   * Mark SLA as met
   */
  async markAsMet(entityType: string, entityId: string, slaType: SlaType): Promise<void> {
    const { error } = await supabase
      .from('sla_tracking')
      .update({
        status: 'met',
        actual_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('sla_type', slaType)
      .eq('status', 'pending');

    if (error) {
      console.error('[SlaMonitoringService] Error marking SLA as met:', error);
    }
  }

  /**
   * Cancel an SLA (e.g., flow cancelled)
   */
  async cancelSla(entityType: string, entityId: string, slaType?: SlaType): Promise<void> {
    let query = supabase
      .from('sla_tracking')
      .update({
        status: 'cancelled',
        updated_at: new Date().toISOString(),
      })
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('status', 'pending');

    if (slaType) {
      query = query.eq('sla_type', slaType);
    }

    const { error } = await query;
    if (error) {
      console.error('[SlaMonitoringService] Error cancelling SLA:', error);
    }
  }

  /**
   * Get all SLAs for an entity
   */
  async getEntitySlas(entityType: string, entityId: string): Promise<SlaRecord[]> {
    const { data, error } = await supabase
      .from('sla_tracking')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to get entity SLAs: ${error.message}`);
    }

    return (data || []).map(this.mapSlaRecord);
  }

  /**
   * Get at-risk or breached SLAs for a tenant
   */
  async getAtRiskSlas(platformTenantId: string): Promise<SlaRecord[]> {
    const { data, error } = await supabase
      .from('sla_tracking')
      .select('*')
      .eq('platform_tenant_id', platformTenantId)
      .in('status', ['at_risk', 'breached', 'pending'])
      .lt('target_date', new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()) // Within 24 hours
      .order('target_date', { ascending: true });

    if (error) {
      throw new Error(`Failed to get at-risk SLAs: ${error.message}`);
    }

    return (data || []).map(this.mapSlaRecord);
  }

  /**
   * Get SLA statistics for a tenant
   */
  async getSlaStats(platformTenantId: string, daysBack = 30): Promise<SlaStats> {
    const since = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('sla_tracking')
      .select('status')
      .eq('platform_tenant_id', platformTenantId)
      .gte('created_at', since);

    if (error) {
      throw new Error(`Failed to get SLA stats: ${error.message}`);
    }

    const stats: SlaStats = {
      total: data?.length || 0,
      pending: 0,
      met: 0,
      breached: 0,
      atRisk: 0,
      metPercentage: 0,
    };

    (data || []).forEach(record => {
      switch (record.status) {
        case 'pending': stats.pending++; break;
        case 'met': stats.met++; break;
        case 'breached': stats.breached++; break;
        case 'at_risk': stats.atRisk++; break;
      }
    });

    const completed = stats.met + stats.breached;
    stats.metPercentage = completed > 0 ? Math.round((stats.met / completed) * 100) : 100;

    return stats;
  }

  /**
   * Check and update SLA statuses (run periodically)
   */
  async checkAndUpdateStatuses(platformTenantId?: string): Promise<{
    checked: number;
    atRisk: number;
    breached: number;
  }> {
    // Get pending SLAs
    let query = supabase
      .from('sla_tracking')
      .select('*')
      .eq('status', 'pending');

    if (platformTenantId) {
      query = query.eq('platform_tenant_id', platformTenantId);
    }

    const { data: pendingSlas, error } = await query;

    if (error) {
      throw new Error(`Failed to check SLAs: ${error.message}`);
    }

    let atRiskCount = 0;
    let breachedCount = 0;

    const now = new Date();

    for (const sla of pendingSlas || []) {
      const targetDate = new Date(sla.target_date);
      const timeRemaining = targetDate.getTime() - now.getTime();
      const createdAt = sla.created_at ? new Date(sla.created_at).getTime() : now.getTime();
      const totalTime = targetDate.getTime() - createdAt;
      const percentUsed = totalTime > 0 ? 1 - (timeRemaining / totalTime) : 1;

      let newStatus: SlaStatus | null = null;

      if (timeRemaining <= 0) {
        // Breached
        newStatus = 'breached';
        breachedCount++;
      } else if (percentUsed >= 0.75) {
        // At risk (75% of time used)
        if (sla.status !== 'at_risk') {
          newStatus = 'at_risk';
          atRiskCount++;
        }
      }

      if (newStatus && newStatus !== sla.status) {
        await supabase
          .from('sla_tracking')
          .update({
            status: newStatus,
            updated_at: new Date().toISOString(),
            ...(newStatus === 'breached' && !sla.breach_notified_at
              ? { breach_notified_at: new Date().toISOString() }
              : {}),
            ...(newStatus === 'at_risk' && !sla.warning_notified_at
              ? { warning_notified_at: new Date().toISOString() }
              : {}),
          })
          .eq('id', sla.id);
      }
    }

    return {
      checked: pendingSlas?.length || 0,
      atRisk: atRiskCount,
      breached: breachedCount,
    };
  }

  /**
   * Create onboarding SLA when flow is created
   */
  async createOnboardingSla(
    flowId: string,
    platformTenantId: string,
    clientId?: string
  ): Promise<SlaRecord> {
    return this.createSla({
      entityType: 'onboarding_flow',
      entityId: flowId,
      slaType: 'onboarding_completion',
      platformTenantId,
      clientId,
      metadata: { flow_id: flowId },
    });
  }

  /**
   * Create credit decision SLA when application is submitted
   */
  async createCreditDecisionSla(
    applicationId: string,
    platformTenantId: string,
    clientId?: string
  ): Promise<SlaRecord> {
    return this.createSla({
      entityType: 'credit_application',
      entityId: applicationId,
      slaType: 'credit_decision',
      platformTenantId,
      clientId,
      metadata: { application_id: applicationId },
    });
  }

  private async getSlaTargetHours(platformTenantId: string, slaType: SlaType): Promise<number> {
    try {
      const mappedType = slaType === 'onboarding_completion' 
        ? 'onboarding' 
        : slaType === 'credit_decision' 
          ? 'credit_decision'
          : 'document_review';
      
      const target = await BusinessRulesEngine.getSlaTarget(platformTenantId, mappedType as any);
      return target.targetHours;
    } catch {
      // Defaults
      switch (slaType) {
        case 'onboarding_completion': return 14 * 24; // 14 days
        case 'credit_decision': return 24; // 24 hours
        case 'document_review': return 48; // 48 hours
        case 'role_completion': return 7 * 24; // 7 days
        default: return 72; // 3 days
      }
    }
  }

  private mapSlaRecord(data: any): SlaRecord {
    return {
      id: data.id,
      entityType: data.entity_type,
      entityId: data.entity_id,
      slaType: data.sla_type,
      targetDate: new Date(data.target_date),
      actualDate: data.actual_date ? new Date(data.actual_date) : null,
      status: data.status,
      breachNotifiedAt: data.breach_notified_at ? new Date(data.breach_notified_at) : null,
      warningNotifiedAt: data.warning_notified_at ? new Date(data.warning_notified_at) : null,
      platformTenantId: data.platform_tenant_id,
      clientId: data.client_id,
      metadata: data.metadata || {},
      createdAt: new Date(data.created_at),
    };
  }
}

export const SlaMonitoringService = new SlaMonitoringServiceClass();
export default SlaMonitoringService;
